import _, { merge } from 'lodash'
import { obj } from '@/util'
import store from '@/store'

export class Collection {

	statePath = ''
	autoSortRule = {}

	constructor({ statePath, autoSortRule, keepUniqueBy } = {
		statePath: '',
		autoSortRule: {},
		keepUniqueBy: 'id'
	}) {
		this.statePath = statePath
		this.autoSortRule = _.pick(autoSortRule, ['key', 'asc'])
		this.keepUniqueBy = keepUniqueBy
		return this;
	}

	state = () => (!!this.statePath ? obj.getWithDot(store.state, this.statePath) : {})

	setState = (data = {}) => obj.setWithDot(store.state, this.statePath, data)

	static stateDefaults = () => ({
		pagination: {},
		items: [],
	})

	data = {}

	/*
	|-------------------------------------------------------------
	| Methods
	|-------------------------------------------------------------
	*/

	commit = {

		toState: (data = {}) => {
			let original = _.merge({}, this.constructor.stateDefaults(), (this.data || {}))
			this.data = obj.cleanCopy(_.merge(original, data))
			this.setState(this.data)
			return this.data
		},

		resetState: () => {
			this.data = {}
			this.setState(this.constructor.stateDefaults())
			return this.data
		},

		items: (data = []) => this.items.commitToState(data)

	}

	items = {

		commitToState: () => this.commit.toState(),

		get: () => (this.state().items || []),

		set: (data = []) => {
			let { items } = this.pagination.detectPagination(data)
			this.data.items = items
			this.items.postProcesses.keepUnique().autoSort()
			return this.items
		},

		toArray: () => (obj.cleanCopy(this.items.get()) || []),

		count: () => (this.items.get().length),

		first: () => _.first(this.items.get()),

		last: () => _.last(this.items.get()),

		findById: (id = 0) => (this.items.get().find(i => i.id == id) || false),

		filter: (key = 'id', value = 0) => (this.items.get().filter(g => g[key] == value)),

		reject: (key = 'id', value = 0) => (this.items.get().filter(g => g[key] != value)),

		updateItem: (obj = {}, matchWith = 'id') => {
			this.data.items = this.items.get().map((i) => {
				if (i[matchWith] == obj[matchWith]) {
					return Object.assign({}, i, obj);
				}
				return i;
			});
			this.items.postProcess()
			return this.items
		},

		deleteItem: (obj = {}, matchWith = 'id') => {
			this.data.items = this.items.get().filter(i => i[matchWith] != obj[matchWith]);
			this.items.postProcess()
			return this.items
		},

		prepend: (obj = {}) => {
			let items = this.data.items || this.items.get()
			items.unshift(obj);
			this.data.items = items
			this.items.postProcess()
			return this.items
		},

		prepends: (objs = [], checkForPagination = true) => {
			let { items } = checkForPagination ? this.pagination.detectPagination(objs) : { items: objs }
			this.data.items = items.length ? items.concat(this.items.get()) : this.items.get()
			this.items.postProcesses.keepUnique().autoSort()
			if (!checkForPagination) {
				this.items.postProcesses.updatePagination()
			}
			return this.items;
		},

		append: (obj = {}) => {
			this.data.items.push(obj);
			this.items.postProcess()
			return this.items
		},

		appends: (objs = [], checkForPagination = true) => {
			let { items } = checkForPagination ? this.pagination.detectPagination(objs) : { items: objs }
			this.data.items = items.length ? this.items.get().concat(items) : this.items.get()
			this.items.postProcesses.keepUnique().autoSort()
			if (!checkForPagination) {
				this.items.postProcesses.updatePagination()
			}
			return this.items;
		},

		slice: (start = 0, end = null) => {
			this.data.items = this.items.get().slice(start, end || undefined)
			return this.items
		},

		sort: (key, asc = true) => {
			if (key) { this.data.items = obj.sortItems(this.data.items, key, asc) }
			return this.items
		},

		postProcess: () => {
			this.items.postProcesses.keepUnique().updatePagination().autoSort()
		},

		postProcesses: {
			keepUnique: () => {
				if (!!this.keepUniqueBy) {
					this.data.items = _.uniqBy(this.data.items, this.keepUniqueBy);
				}
				return this.items.postProcesses
			},
			updatePagination: () => {
				this.pagination.update()
				return this.items.postProcesses
			},
			autoSort: () => {
				let { key, asc } = this.autoSortRule
				this.items.sort(key, asc)
				return this.items.postProcesses
			}
		}
	}

	pagination = {

		get: () => (this.state().pagination || {}),

		inUse: () => !!this.data.paginationInUse,

		currentPage: () => (this.pagination.get().current_page),

		nextPage: () => (this.pagination.isLastPage() ? false : this.pagination.currentPage() + 1),

		isFirstPage: () => (this.items.get().length <= this.pagination.get().per_page),

		isLastPage: () => (this.pagination.currentPage() == this.pagination.get().last_page),

		detectPagination: (data = {}) => {
			this.data.paginationInUse = (!!data.data && !!data.current_page)

			let items = (this.pagination.inUse() ? data.data : data)

			this.pagination.update(data)

			return {
				items,
				pagination: this.data.pagination
			}
		},

		update: (newPaginationMeta = {}) => {
			if (!this.pagination.inUse()) { return this.pagination }

			let paginationMeta = !!newPaginationMeta.current_page ? newPaginationMeta : this.pagination.get()

			if (!newPaginationMeta.current_page) {
				let { current_page, last_page, total, to, per_page } = paginationMeta
				// Calculate

				// NOTE: DELETE THIS NOTE AFTER 1 OCTOBER IF TOTAL NUMBER OF ITEMS IN LIST IS CORRECT
				let change = (this.data.items.length - to)
				// total = (total + change)
				// TODO: Fix this total, it is wrong.
				// It should not count duplicates, but it does.

				// This should fix it:
				total = this.data.items.length

				last_page = Math.ceil(total / per_page)
				// Trim
				let max_items = (per_page * current_page)
				if (this.items.get().length > max_items) { this.data.items = this.items.get().slice(0, max_items); }
				//
				to = Math.max(this.data.items.length, total)
				current_page = Math.ceil(to / per_page)

				// Update
				paginationMeta = { total, last_page, current_page, to }
			}

			this.data.pagination = Object.assign(
				{},
				this.pagination.get(),
				_.pick(paginationMeta, ['total', 'per_page', 'current_page', 'last_page', 'to', 'from'])
			);

			return this.pagination
		}

	}
}
